import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Lock, CheckCircle, ArrowRight, ArrowLeft, Trophy, Sparkles, RotateCcw, BookOpen, GraduationCap, ChevronLeft, ChevronRight, Volume2, Mic, MicOff } from "lucide-react";
import { useSpeech } from "@/hooks/useSpeech";

interface DrMathGameProps {
  onComplete?: (result: { score: number; maxScore: number; stars: number }) => void;
}

interface Level {
  id: number;
  title: string;
  description: string;
  emoji: string;
  color: string;
  generateQuestion: () => Question;
  questionsCount: number;
}

interface Question {
  text: string;
  displayText: string;
  options: number[];
  correctAnswer: number;
}

interface LessonPage {
  title: string;
  content: string;
  illustration: string;
  tip?: string;
}

const toArabicNum = (n: number): string =>
  n.toString().replace(/\d/g, (d) => "٠١٢٣٤٥٦٧٨٩"[parseInt(d)]);

const shuffle = <T,>(arr: T[]): T[] => [...arr].sort(() => Math.random() - 0.5);

const generateOptions = (correct: number, count = 4, range = 5): number[] => {
  const opts = new Set<number>([correct]);
  let attempts = 0;
  while (opts.size < count && attempts < 50) {
    const offset = Math.floor(Math.random() * range * 2) - range;
    const val = correct + offset;
    if (val >= 0 && val !== correct) opts.add(val);
    attempts++;
  }
  while (opts.size < count) opts.add(opts.size * 2);
  return shuffle([...opts]);
};

// ─── Lesson content for each level ───
const lessonContent: Record<number, LessonPage[]> = {
  1: [
    {
      title: "تعرّف على الأرقام! 🔢",
      content: "الأرقام هي أصدقاؤنا! كل رقم له اسم وشكل خاص. هيا نتعرف عليها من ١ إلى ١٠.",
      illustration: "١ ② ③ ④ ⑤ ⑥ ⑦ ⑧ ⑨ ⑩",
    },
    {
      title: "العدّ بالأصابع ✋",
      content: "يمكننا عدّ الأشياء باستخدام أصابعنا! كل إصبع يمثل رقماً واحداً.",
      illustration: "☝️ = ١\n✌️ = ٢\n🤟 = ٣\n🖐️ = ٥\n🖐️☝️ = ٦",
      tip: "جرّب أن تعدّ ألعابك في المنزل!",
    },
    {
      title: "هيا نعدّ! 🍎",
      content: "لنعدّ التفاحات معاً:",
      illustration: "🍎 = ١\n🍎🍎 = ٢\n🍎🍎🍎 = ٣\n🍎🍎🍎🍎 = ٤\n🍎🍎🍎🍎🍎 = ٥\n🍎🍎🍎🍎🍎🍎 = ٦\n🍎🍎🍎🍎🍎🍎🍎 = ٧\n🍎🍎🍎🍎🍎🍎🍎🍎 = ٨\n🍎🍎🍎🍎🍎🍎🍎🍎🍎 = ٩\n🍎🍎🍎🍎🍎🍎🍎🍎🍎🍎 = ١٠",
      tip: "كل مرة نُضيف تفاحة واحدة يزيد الرقم بواحد!",
    },
  ],
  2: [
    {
      title: "الأرقام الكبيرة! 🔟",
      content: "بعد الرقم ١٠ تأتي أرقام أكبر. الرقم ١١ يعني ١٠ + ١، والرقم ١٢ يعني ١٠ + ٢.",
      illustration: "🔵🔵🔵🔵🔵🔵🔵🔵🔵🔵 = ١٠\n🔵🔵🔵🔵🔵🔵🔵🔵🔵🔵 🔴 = ١١\n🔵🔵🔵🔵🔵🔵🔵🔵🔵🔵 🔴🔴 = ١٢",
    },
    {
      title: "نكمل العدّ 📊",
      content: "كل رقم بعد ١٠ هو ١٠ زائد رقم صغير:",
      illustration: "١٣ = ١٠ + ٣ 🟢🟢🟢\n١٤ = ١٠ + ٤ 🟢🟢🟢🟢\n١٥ = ١٠ + ٥ 🟢🟢🟢🟢🟢\n١٦ = ١٠ + ٦ 🟢🟢🟢🟢🟢🟢\n١٧ = ١٠ + ٧ 🟢🟢🟢🟢🟢🟢🟢\n١٨ = ١٠ + ٨ 🟢🟢🟢🟢🟢🟢🟢🟢\n١٩ = ١٠ + ٩ 🟢🟢🟢🟢🟢🟢🟢🟢🟢\n٢٠ = ١٠ + ١٠ 🟢🟢🟢🟢🟢🟢🟢🟢🟢🟢",
      tip: "الرقم ٢٠ يعني عشرتين!",
    },
  ],
  3: [
    {
      title: "ما هو الجمع؟ ➕",
      content: "الجمع يعني أننا نضم مجموعتين معاً لنحصل على مجموعة أكبر.",
      illustration: "🍎🍎 + 🍎🍎🍎 = 🍎🍎🍎🍎🍎\n٢ + ٣ = ٥",
    },
    {
      title: "أمثلة على الجمع 🌟",
      content: "لنتعلم الجمع بالأمثلة:",
      illustration: "⭐ + ⭐⭐ = ⭐⭐⭐     (١ + ٢ = ٣)\n🌸🌸 + 🌸🌸 = 🌸🌸🌸🌸     (٢ + ٢ = ٤)\n🐟🐟🐟 + 🐟🐟 = 🐟🐟🐟🐟🐟     (٣ + ٢ = ٥)\n🦋🦋🦋 + 🦋🦋🦋 = 🦋🦋🦋🦋🦋🦋     (٣ + ٣ = ٦)",
      tip: "الجمع سهل! فقط اعدّ الأشياء كلها معاً.",
    },
    {
      title: "خدعة الأصابع ✋➕✋",
      content: "ارفع أصابع يدك اليمنى بالرقم الأول، ثم أصابع يدك اليسرى بالرقم الثاني، واعدّ كل الأصابع!",
      illustration: "مثال: ٤ + ٣\n✋ = ارفع ٤ أصابع\n🤟 = ارفع ٣ أصابع\nاعدّ الكل = ٧ ✅",
      tip: "هذه الطريقة تنفع للأرقام حتى ١٠!",
    },
  ],
  4: [
    {
      title: "ما هو الطرح؟ ➖",
      content: "الطرح يعني أننا نأخذ بعض الأشياء من مجموعة. يبقى عدد أقل!",
      illustration: "🍎🍎🍎🍎🍎 - 🍎🍎 = 🍎🍎🍎\n٥ - ٢ = ٣",
    },
    {
      title: "أمثلة على الطرح 📉",
      content: "نشطب الأشياء التي نطرحها:",
      illustration: "🌸🌸🌸🌸 - ١ = 🌸🌸🌸     (٤ - ١ = ٣)\n⭐⭐⭐⭐⭐ - ٣ = ⭐⭐     (٥ - ٣ = ٢)\n🐟🐟🐟🐟🐟🐟 - ٤ = 🐟🐟     (٦ - ٤ = ٢)\n🎈🎈🎈🎈🎈🎈🎈 - ٥ = 🎈🎈     (٧ - ٥ = ٢)",
      tip: "الطرح عكس الجمع! إذا ٣ + ٢ = ٥، إذاً ٥ - ٢ = ٣",
    },
  ],
  5: [
    {
      title: "الجمع والطرح معاً 🧮",
      content: "الآن سنتعامل مع أرقام أكبر! نستخدم نفس القواعد لكن مع أرقام أكبر من ١٠.",
      illustration: "١٥ + ٣ = ١٨\n٢٠ - ٧ = ١٣\n١٢ + ٨ = ٢٠\n٢٥ - ١٠ = ١٥",
    },
    {
      title: "حيلة الحساب السريع ⚡",
      content: "عند جمع أو طرح ١٠، فقط غيّر رقم العشرات:",
      illustration: "١٥ + ١٠ = ٢٥ (غيّرنا ١ إلى ٢)\n٢٣ - ١٠ = ١٣ (غيّرنا ٢ إلى ١)\n٣٧ + ١٠ = ٤٧\n٤٢ - ١٠ = ٣٢",
      tip: "جمع أو طرح ١٠ من أسهل العمليات!",
    },
  ],
  6: [
    {
      title: "ما هو الضرب؟ ✖️",
      content: "الضرب هو جمع متكرر! بدل أن نقول ٣ + ٣ + ٣ + ٣، نقول ٣ × ٤ = ١٢",
      illustration: "٣ × ٢ = ٣ + ٣ = ٦\n🍎🍎🍎  🍎🍎🍎\n\n٤ × ٣ = ٤ + ٤ + ٤ = ١٢\n🔵🔵🔵🔵  🔵🔵🔵🔵  🔵🔵🔵🔵",
    },
    {
      title: "جدول ضرب ٢ و ٣ 📋",
      content: "لنحفظ جدول الضرب!",
      illustration: "٢ × ١ = ٢    |    ٣ × ١ = ٣\n٢ × ٢ = ٤    |    ٣ × ٢ = ٦\n٢ × ٣ = ٦    |    ٣ × ٣ = ٩\n٢ × ٤ = ٨    |    ٣ × ٤ = ١٢\n٢ × ٥ = ١٠   |    ٣ × ٥ = ١٥",
      tip: "جدول ٢ سهل: كل مرة نزيد ٢!",
    },
    {
      title: "جدول ضرب ٤ و ٥ 📋",
      content: "نكمل مع جداول ٤ و ٥:",
      illustration: "٤ × ١ = ٤    |    ٥ × ١ = ٥\n٤ × ٢ = ٨    |    ٥ × ٢ = ١٠\n٤ × ٣ = ١٢   |    ٥ × ٣ = ١٥\n٤ × ٤ = ١٦   |    ٥ × ٤ = ٢٠\n٤ × ٥ = ٢٠   |    ٥ × ٥ = ٢٥",
      tip: "جدول ٥: كل نتيجة تنتهي بـ ٠ أو ٥!",
    },
  ],
  7: [
    {
      title: "ما هي القسمة؟ ➗",
      content: "القسمة تعني توزيع الأشياء بالتساوي. إذا عندنا ٦ تفاحات ونريد توزيعها على ٣ أطفال:",
      illustration: "🍎🍎🍎🍎🍎🍎 ÷ ٣ أطفال\n\n👦 ← 🍎🍎\n👧 ← 🍎🍎\n👦 ← 🍎🍎\n\n٦ ÷ ٣ = ٢ لكل طفل!",
    },
    {
      title: "القسمة عكس الضرب 🔄",
      content: "إذا كنت تعرف الضرب، تعرف القسمة!",
      illustration: "٣ × ٤ = ١٢  ←→  ١٢ ÷ ٣ = ٤\n٢ × ٥ = ١٠  ←→  ١٠ ÷ ٢ = ٥\n٤ × ٣ = ١٢  ←→  ١٢ ÷ ٤ = ٣\n٥ × ٢ = ١٠  ←→  ١٠ ÷ ٥ = ٢",
      tip: "القسمة = الضرب بالعكس!",
    },
  ],
  8: [
    {
      title: "أنت بطل الرياضيات! 🏆",
      content: "في هذا المستوى ستواجه جميع العمليات: جمع، طرح، ضرب، وقسمة. استخدم كل ما تعلمته!",
      illustration: "➕ الجمع: ضم مجموعتين\n➖ الطرح: أخذ من مجموعة\n✖️ الضرب: جمع متكرر\n➗ القسمة: توزيع بالتساوي",
    },
    {
      title: "نصائح للحل السريع 🚀",
      content: "استخدم هذه الحيل:",
      illustration: "✅ الجمع: ابدأ بالرقم الأكبر واعدّ\n✅ الطرح: فكّر بالجمع العكسي\n✅ الضرب: تذكّر جداول الضرب\n✅ القسمة: فكّر بالضرب العكسي",
      tip: "لا تستعجل! فكّر جيداً قبل الإجابة 🧠",
    },
  ],
};

// ─── Level definitions ───
const levels: Level[] = [
  {
    id: 1, title: "المستوى ١: الأرقام ١-١٠", description: "تعرّف على الأرقام من ١ إلى ١٠",
    emoji: "🔢", color: "from-green-400 to-emerald-500", questionsCount: 8,
    generateQuestion: () => {
      const num = Math.floor(Math.random() * 10) + 1;
      const items = ["🍎", "⭐", "🌸", "🐟", "🦋"][Math.floor(Math.random() * 5)];
      return { text: `${num}`, displayText: `كم عدد ${items.repeat(num)}؟`, options: generateOptions(num, 4, 3), correctAnswer: num };
    },
  },
  {
    id: 2, title: "المستوى ٢: الأرقام ١١-٢٠", description: "تعرّف على الأرقام من ١١ إلى ٢٠",
    emoji: "🔟", color: "from-blue-400 to-cyan-500", questionsCount: 8,
    generateQuestion: () => {
      const num = Math.floor(Math.random() * 10) + 11;
      const row1 = "⬛".repeat(10);
      const row2 = "⬛".repeat(num - 10);
      return { text: `${num}`, displayText: `كم مربعاً هنا؟\n${row1}\n${row2}`, options: generateOptions(num, 4, 3), correctAnswer: num };
    },
  },
  {
    id: 3, title: "المستوى ٣: الجمع البسيط", description: "جمع أرقام من ١ إلى ٩",
    emoji: "➕", color: "from-yellow-400 to-amber-500", questionsCount: 10,
    generateQuestion: () => {
      const a = Math.floor(Math.random() * 9) + 1, b = Math.floor(Math.random() * 9) + 1;
      return { text: `${a} + ${b}`, displayText: `${toArabicNum(a)} + ${toArabicNum(b)} = ؟`, options: generateOptions(a + b, 4, 4), correctAnswer: a + b };
    },
  },
  {
    id: 4, title: "المستوى ٤: الطرح البسيط", description: "طرح أرقام بسيطة",
    emoji: "➖", color: "from-orange-400 to-red-400", questionsCount: 10,
    generateQuestion: () => {
      const b = Math.floor(Math.random() * 9) + 1, a = b + Math.floor(Math.random() * 9) + 1;
      return { text: `${a} - ${b}`, displayText: `${toArabicNum(a)} - ${toArabicNum(b)} = ؟`, options: generateOptions(a - b, 4, 4), correctAnswer: a - b };
    },
  },
  {
    id: 5, title: "المستوى ٥: الجمع والطرح معاً", description: "عمليات جمع وطرح بأرقام أكبر",
    emoji: "🧮", color: "from-purple-400 to-violet-500", questionsCount: 10,
    generateQuestion: () => {
      const isAdd = Math.random() > 0.5;
      if (isAdd) { const a = Math.floor(Math.random() * 20) + 5, b = Math.floor(Math.random() * 15) + 1; return { text: `${a} + ${b}`, displayText: `${toArabicNum(a)} + ${toArabicNum(b)} = ؟`, options: generateOptions(a + b, 4, 6), correctAnswer: a + b }; }
      const b = Math.floor(Math.random() * 15) + 1, a = b + Math.floor(Math.random() * 20) + 1;
      return { text: `${a} - ${b}`, displayText: `${toArabicNum(a)} - ${toArabicNum(b)} = ؟`, options: generateOptions(a - b, 4, 6), correctAnswer: a - b };
    },
  },
  {
    id: 6, title: "المستوى ٦: مقدمة الضرب", description: "جداول الضرب من ١ إلى ٥",
    emoji: "✖️", color: "from-pink-400 to-rose-500", questionsCount: 10,
    generateQuestion: () => {
      const a = Math.floor(Math.random() * 5) + 1, b = Math.floor(Math.random() * 5) + 1;
      return { text: `${a} × ${b}`, displayText: `${toArabicNum(a)} × ${toArabicNum(b)} = ؟`, options: generateOptions(a * b, 4, 5), correctAnswer: a * b };
    },
  },
  {
    id: 7, title: "المستوى ٧: مقدمة القسمة", description: "قسمة بسيطة بدون باقي",
    emoji: "➗", color: "from-teal-400 to-emerald-500", questionsCount: 10,
    generateQuestion: () => {
      const b = Math.floor(Math.random() * 5) + 2, result = Math.floor(Math.random() * 5) + 1, a = b * result;
      return { text: `${a} ÷ ${b}`, displayText: `${toArabicNum(a)} ÷ ${toArabicNum(b)} = ؟`, options: generateOptions(result, 4, 4), correctAnswer: result };
    },
  },
  {
    id: 8, title: "المستوى ٨: العمليات المختلطة", description: "جمع وطرح وضرب وقسمة معاً",
    emoji: "🏆", color: "from-amber-500 to-yellow-600", questionsCount: 12,
    generateQuestion: () => {
      const op = Math.floor(Math.random() * 4);
      if (op === 0) { const a = Math.floor(Math.random() * 50) + 10, b = Math.floor(Math.random() * 30) + 1; return { text: `${a} + ${b}`, displayText: `${toArabicNum(a)} + ${toArabicNum(b)} = ؟`, options: generateOptions(a + b, 4, 8), correctAnswer: a + b }; }
      if (op === 1) { const b = Math.floor(Math.random() * 25) + 1, a = b + Math.floor(Math.random() * 30) + 1; return { text: `${a} - ${b}`, displayText: `${toArabicNum(a)} - ${toArabicNum(b)} = ؟`, options: generateOptions(a - b, 4, 8), correctAnswer: a - b }; }
      if (op === 2) { const a = Math.floor(Math.random() * 9) + 2, b = Math.floor(Math.random() * 9) + 2; return { text: `${a} × ${b}`, displayText: `${toArabicNum(a)} × ${toArabicNum(b)} = ؟`, options: generateOptions(a * b, 4, 10), correctAnswer: a * b }; }
      const b = Math.floor(Math.random() * 8) + 2, result = Math.floor(Math.random() * 8) + 1, a = b * result;
      return { text: `${a} ÷ ${b}`, displayText: `${toArabicNum(a)} ÷ ${toArabicNum(b)} = ؟`, options: generateOptions(result, 4, 5), correctAnswer: result };
    },
  },
];

type ViewMode = "levels" | "lesson" | "exam" | "result";

const DrMathGame = ({ onComplete }: DrMathGameProps) => {
  const [unlockedLevel, setUnlockedLevel] = useState(1);
  const [activeLevel, setActiveLevel] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("levels");
  const [lessonPage, setLessonPage] = useState(0);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [levelStars, setLevelStars] = useState<Record<number, number>>({});

  const { speak, isListening, startListening, stopListening, encourageCorrect, encourageWrong } = useSpeech({
    onVoiceResult: (transcript) => {
      if (viewMode !== "exam" || selectedAnswer !== null) return;
      const arabicToNum: Record<string, string> = { "٠": "0", "١": "1", "٢": "2", "٣": "3", "٤": "4", "٥": "5", "٦": "6", "٧": "7", "٨": "8", "٩": "9" };
      const normalized = transcript.replace(/[٠-٩]/g, d => arabicToNum[d] || d);
      const nums = normalized.match(/\d+/g);
      if (nums) {
        const spoken = parseInt(nums[0]);
        const q = questions[currentQ];
        if (q && q.options.includes(spoken)) handleAnswer(spoken);
      }
    },
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem("drmath-progress");
      if (saved) {
        const data = JSON.parse(saved);
        setUnlockedLevel(data.unlockedLevel || 1);
        setLevelStars(data.levelStars || {});
      }
    } catch {}
  }, []);

  const saveProgress = useCallback((unlocked: number, stars: Record<number, number>) => {
    localStorage.setItem("drmath-progress", JSON.stringify({ unlockedLevel: unlocked, levelStars: stars }));
  }, []);

  const openLesson = (levelId: number) => {
    setActiveLevel(levelId);
    setLessonPage(0);
    setViewMode("lesson");
  };

  const startExam = (levelId: number) => {
    const level = levels.find((l) => l.id === levelId);
    if (!level) return;
    const qs = Array.from({ length: level.questionsCount }, () => level.generateQuestion());
    setQuestions(qs);
    setCurrentQ(0);
    setScore(0);
    setSelectedAnswer(null);
    setIsCorrect(null);
    setActiveLevel(levelId);
    setViewMode("exam");
  };

  const handleAnswer = (answer: number) => {
    if (selectedAnswer !== null) return;
    const correct = answer === questions[currentQ].correctAnswer;
    setSelectedAnswer(answer);
    setIsCorrect(correct);
    if (correct) {
      setScore((s) => s + 1);
      encourageCorrect();
    } else {
      encourageWrong();
    }

    setTimeout(() => {
      if (currentQ + 1 < questions.length) {
        setCurrentQ((q) => q + 1);
        setSelectedAnswer(null);
        setIsCorrect(null);
      } else {
        const finalScore = correct ? score + 1 : score;
        const maxScore = questions.length;
        const pct = (finalScore / maxScore) * 100;
        const stars = pct >= 90 ? 3 : pct >= 70 ? 2 : pct >= 50 ? 1 : 0;

        if (stars >= 1 && activeLevel! < 8) {
          const newUnlocked = Math.max(unlockedLevel, activeLevel! + 1);
          const newStars = { ...levelStars, [activeLevel!]: Math.max(levelStars[activeLevel!] || 0, stars) };
          setUnlockedLevel(newUnlocked);
          setLevelStars(newStars);
          saveProgress(newUnlocked, newStars);
        } else if (stars >= 1 && activeLevel === 8) {
          const newStars = { ...levelStars, 8: Math.max(levelStars[8] || 0, stars) };
          setLevelStars(newStars);
          saveProgress(unlockedLevel, newStars);
        }

        setViewMode("result");
        onComplete?.({ score: finalScore, maxScore, stars });
      }
    }, 1200);
  };

  // ─── Level Selection ───
  if (viewMode === "levels") {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground">🧮 منهج دكتور ماس</h2>
          <p className="text-muted-foreground text-sm mt-1">٨ مستويات متدرجة — لكل مستوى درس وامتحان</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {levels.map((level) => {
            const isLocked = level.id > unlockedLevel;
            const stars = levelStars[level.id] || 0;
            return (
              <motion.div
                key={level.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: level.id * 0.08 }}
              >
                <Card
                  className={`overflow-hidden border-2 transition-all ${
                    isLocked ? "opacity-50 border-muted cursor-not-allowed" : "border-transparent shadow-card hover:shadow-elevated"
                  }`}
                >
                  <CardContent className="p-0">
                    <div className={`bg-gradient-to-br ${level.color} p-4 text-white relative`}>
                      {isLocked && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <Lock className="w-8 h-8" />
                        </div>
                      )}
                      <p className="text-3xl mb-1">{level.emoji}</p>
                      <h3 className="text-sm font-bold">{level.title}</h3>
                      <p className="text-xs opacity-90 mt-1">{level.description}</p>
                      {stars > 0 && (
                        <div className="flex gap-0.5 mt-2">
                          {[1, 2, 3].map((s) => (
                            <Star key={s} className={`w-4 h-4 ${s <= stars ? "fill-yellow-300 text-yellow-300" : "text-white/30"}`} />
                          ))}
                        </div>
                      )}
                    </div>
                    {!isLocked && (
                      <div className="flex border-t border-border">
                        <Button
                          variant="ghost"
                          className="flex-1 rounded-none h-10 text-xs gap-1"
                          onClick={() => openLesson(level.id)}
                        >
                          <BookOpen className="w-3.5 h-3.5" />
                          الدرس
                        </Button>
                        <div className="w-px bg-border" />
                        <Button
                          variant="ghost"
                          className="flex-1 rounded-none h-10 text-xs gap-1"
                          onClick={() => startExam(level.id)}
                        >
                          <GraduationCap className="w-3.5 h-3.5" />
                          الامتحان
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  }

  const level = levels.find((l) => l.id === activeLevel)!;
  const pages = lessonContent[activeLevel!] || [];

  // ─── Lesson View ───
  if (viewMode === "lesson") {
    const page = pages[lessonPage];
    const totalPages = pages.length;

    return (
      <div className="space-y-4 max-w-2xl mx-auto">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => setViewMode("levels")}>
            <ArrowRight className="w-4 h-4 ml-1" />
            رجوع
          </Button>
          <span className="text-sm font-medium text-muted-foreground">{level.emoji} {level.title}</span>
          <span className="text-sm text-muted-foreground">
            {toArabicNum(lessonPage + 1)} / {toArabicNum(totalPages)}
          </span>
        </div>

        <Progress value={((lessonPage + 1) / totalPages) * 100} className="h-2" />

        <AnimatePresence mode="wait">
          <motion.div
            key={lessonPage}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
          >
            <Card className="shadow-card">
              <CardContent className="p-6 space-y-5">
                <h3 className="text-xl font-bold text-foreground text-center">{page.title}</h3>
                
                <p className="text-base text-foreground/90 leading-relaxed text-center">{page.content}</p>

                <div className="bg-muted/60 rounded-xl p-5 text-center">
                  <pre className="text-base leading-loose whitespace-pre-wrap font-sans text-foreground/80 direction-rtl">
                    {page.illustration}
                  </pre>
                </div>

                {page.tip && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-primary/10 border border-primary/20 rounded-lg p-3 text-center"
                  >
                    <p className="text-sm text-primary font-medium">💡 {page.tip}</p>
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between gap-3">
          <Button
            variant="outline"
            onClick={() => setLessonPage((p) => p - 1)}
            disabled={lessonPage === 0}
          >
            <ChevronRight className="w-4 h-4 ml-1" />
            السابق
          </Button>

          {lessonPage === totalPages - 1 ? (
            <Button
              className="bg-gradient-to-r from-primary to-primary/80"
              onClick={() => startExam(activeLevel!)}
            >
              <GraduationCap className="w-4 h-4 ml-1" />
              ابدأ الامتحان
            </Button>
          ) : (
            <Button onClick={() => setLessonPage((p) => p + 1)}>
              التالي
              <ChevronLeft className="w-4 h-4 mr-1" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ─── Result Screen ───
  if (viewMode === "result") {
    const maxScore = questions.length;
    const pct = (score / maxScore) * 100;
    const stars = pct >= 90 ? 3 : pct >= 70 ? 2 : pct >= 50 ? 1 : 0;

    return (
      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="flex justify-center py-8">
        <Card className="w-full max-w-md text-center shadow-elevated">
          <CardContent className="p-8 space-y-4">
            <motion.div animate={{ rotate: [0, 10, -10, 0] }} transition={{ repeat: 3, duration: 0.5 }}>
              {stars >= 2 ? <Trophy className="w-16 h-16 mx-auto text-yellow-500" /> : <Sparkles className="w-16 h-16 mx-auto text-primary" />}
            </motion.div>
            <h2 className="text-2xl font-bold text-foreground">
              {stars >= 2 ? "أحسنت! 🎉" : stars >= 1 ? "جيد! 👍" : "حاول مرة أخرى 💪"}
            </h2>
            <p className="text-muted-foreground">{level.title}</p>
            <p className="text-3xl font-bold text-primary">
              {toArabicNum(score)} / {toArabicNum(maxScore)}
            </p>
            <div className="flex justify-center gap-1">
              {[1, 2, 3].map((s) => (
                <motion.div key={s} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: s * 0.2, type: "spring" }}>
                  <Star className={`w-10 h-10 ${s <= stars ? "fill-yellow-400 text-yellow-400" : "text-muted"}`} />
                </motion.div>
              ))}
            </div>
            <div className="flex gap-2 justify-center pt-4 flex-wrap">
              <Button variant="outline" onClick={() => openLesson(activeLevel!)}>
                <BookOpen className="w-4 h-4 ml-1" />
                مراجعة الدرس
              </Button>
              <Button variant="outline" onClick={() => startExam(activeLevel!)}>
                <RotateCcw className="w-4 h-4 ml-1" />
                إعادة الامتحان
              </Button>
              <Button onClick={() => setViewMode("levels")}>
                <ArrowRight className="w-4 h-4 ml-1" />
                المستويات
              </Button>
              {stars >= 1 && activeLevel! < 8 && (
                <Button className="bg-gradient-to-r from-primary to-primary/80" onClick={() => openLesson(activeLevel! + 1)}>
                  المستوى التالي
                  <ArrowLeft className="w-4 h-4 mr-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // ─── Exam (Question) Screen ───
  const q = questions[currentQ];
  const progress = ((currentQ + 1) / questions.length) * 100;

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => setViewMode("levels")}>
          <ArrowRight className="w-4 h-4 ml-1" />
          رجوع
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => speak(q.displayText)}>
            <Volume2 className="w-4 h-4" />
          </Button>
          <Button variant={isListening ? "destructive" : "ghost"} size="icon" className={`h-8 w-8 rounded-full ${isListening ? "animate-pulse" : ""}`} onClick={() => isListening ? stopListening() : startListening()} disabled={selectedAnswer !== null}>
            {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </Button>
        </div>
        <span className="text-sm font-bold text-primary">
          {toArabicNum(score)} / {toArabicNum(questions.length)}
        </span>
      </div>

      <Progress value={progress} className="h-3" />

      <AnimatePresence mode="wait">
        <motion.div
          key={currentQ}
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 30 }}
        >
          <Card className="shadow-card">
            <CardContent className="p-6 text-center space-y-6">
              <p className="text-sm text-muted-foreground">
                السؤال {toArabicNum(currentQ + 1)} من {toArabicNum(questions.length)}
              </p>

              <div className="text-xl font-bold text-foreground whitespace-pre-line leading-relaxed min-h-[80px] flex items-center justify-center">
                {q.displayText}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {q.options.map((opt) => {
                  let extra = "";
                  if (selectedAnswer !== null) {
                    if (opt === q.correctAnswer) {
                      extra = "border-green-500 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300";
                    } else if (opt === selectedAnswer && !isCorrect) {
                      extra = "border-red-500 bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300";
                    }
                  }
                  return (
                    <motion.div key={opt} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        variant="outline"
                        className={`w-full h-14 text-xl font-bold ${extra}`}
                        onClick={() => handleAnswer(opt)}
                        disabled={selectedAnswer !== null}
                      >
                        {toArabicNum(opt)}
                      </Button>
                    </motion.div>
                  );
                })}
              </div>

              {selectedAnswer !== null && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`text-lg font-bold ${isCorrect ? "text-green-600" : "text-red-600"}`}
                >
                  {isCorrect ? "✅ صحيح!" : `❌ الإجابة: ${toArabicNum(q.correctAnswer)}`}
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default DrMathGame;
